/**
 * Primitive Service - Firestore Client
 * 
 * Handles all Firestore operations for primitive flows:
 * - Admin: CRUD operations, publish/unpublish
 * - Public: Browse published primitives, search, categories
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { FlowDefinition, JSONSchema, PrimitiveCapabilityRequirements } from '@simcheck/shared-types';

const COLLECTION_NAME = 'primitives';

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Remove undefined values from an object recursively
 * Firestore doesn't allow undefined values
 */
const removeUndefined = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return null;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(removeUndefined);
  }
  
  if (typeof obj === 'object') {
    const cleaned: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        cleaned[key] = removeUndefined(value);
      }
    }
    return cleaned;
  }
  
  return obj;
};

// ============================================================================
// TYPES
// ============================================================================

export interface CreatePrimitiveParams {
  name: string;
  description?: string;
  category?: string;
  inputSchema?: JSONSchema;
  outputSchema?: JSONSchema;
  capabilityRequirements?: PrimitiveCapabilityRequirements;
  flow: any; // FlowGraph from shared-types
}

export interface UpdatePrimitiveParams {
  name?: string;
  description?: string;
  category?: string;
  inputSchema?: JSONSchema;
  outputSchema?: JSONSchema;
  capabilityRequirements?: PrimitiveCapabilityRequirements;
  flow?: any;
}

export interface PrimitivesResponse {
  primitives: FlowDefinition[];
  count: number;
}

export interface CategoriesResponse {
  categories: string[];
}

export interface SearchResponse {
  primitives: FlowDefinition[];
  count: number;
  query: string;
}

// ============================================================================
// ADMIN OPERATIONS (Super Admin Only)
// ============================================================================

/**
 * Get all primitives (published + unpublished)
 * Super admin only
 */
export const getAllPrimitives = async (): Promise<FlowDefinition[]> => {
  const primitivesRef = collection(db, COLLECTION_NAME);
  const q = query(primitivesRef, orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  } as FlowDefinition));
};

/**
 * Get a specific primitive by ID
 * Super admin only
 */
export const getPrimitive = async (id: string): Promise<FlowDefinition> => {
  const primitiveRef = doc(db, COLLECTION_NAME, id);
  const snapshot = await getDoc(primitiveRef);
  
  if (!snapshot.exists()) {
    throw new Error('Primitive not found');
  }
  
  return {
    id: snapshot.id,
    ...snapshot.data(),
  } as FlowDefinition;
};

/**
 * Create a new primitive
 * Super admin only
 */
export const createPrimitive = async (params: CreatePrimitiveParams): Promise<FlowDefinition> => {
  const { auth } = await import('../config/firebase');
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('User not authenticated');
  }
  
  const primitiveData: Partial<FlowDefinition> = {
    name: params.name,
    description: params.description,
    category: params.category,
    inputSchema: params.inputSchema,
    outputSchema: params.outputSchema,
    flow: params.flow,
    type: 'primitive',
    isPublished: false,
    version: 1,
    createdBy: currentUser.uid,
    tags: [],
    inputs: [],
    outputs: [],
    config: {
      maxExecutionTime: 300000,
      maxRetries: 0,
      resourceRequirements: [],
      notifications: {
        onStart: false,
        onComplete: false,
        onError: false,
        channels: [],
        recipients: [],
      },
    },
    createdAt: serverTimestamp() as any,
    updatedAt: serverTimestamp() as any,
  };
  
  // Clean up undefined values for Firestore
  const cleanedData = removeUndefined(primitiveData);
  
  const docRef = await addDoc(collection(db, COLLECTION_NAME), cleanedData);
  
  return {
    ...primitiveData,
    id: docRef.id,
    createdAt: new Date(),
    updatedAt: new Date(),
  } as FlowDefinition;
};

/**
 * Update an existing primitive
 * Super admin only
 */
export const updatePrimitive = async (
  id: string,
  params: UpdatePrimitiveParams
): Promise<FlowDefinition> => {
  const primitiveRef = doc(db, COLLECTION_NAME, id);
  
  const updateData = {
    ...params,
    updatedAt: serverTimestamp(),
  };
  
  // Clean up undefined values for Firestore
  const cleanedData = removeUndefined(updateData);
  
  await updateDoc(primitiveRef, cleanedData);
  
  const updated = await getDoc(primitiveRef);
  return {
    id: updated.id,
    ...updated.data(),
  } as FlowDefinition;
};

/**
 * Delete a primitive
 * Super admin only
 */
export const deletePrimitive = async (id: string): Promise<void> => {
  const primitiveRef = doc(db, COLLECTION_NAME, id);
  await deleteDoc(primitiveRef);
};

/**
 * Publish a primitive (make it visible to users)
 * Super admin only
 */
export const publishPrimitive = async (id: string): Promise<FlowDefinition> => {
  const primitiveRef = doc(db, COLLECTION_NAME, id);
  
  await updateDoc(primitiveRef, {
    isPublished: true,
    publishedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  
  const updated = await getDoc(primitiveRef);
  return {
    id: updated.id,
    ...updated.data(),
  } as FlowDefinition;
};

/**
 * Unpublish a primitive (hide from users)
 * Super admin only
 */
export const unpublishPrimitive = async (id: string): Promise<FlowDefinition> => {
  const primitiveRef = doc(db, COLLECTION_NAME, id);
  
  await updateDoc(primitiveRef, {
    isPublished: false,
    updatedAt: serverTimestamp(),
  });
  
  const updated = await getDoc(primitiveRef);
  return {
    id: updated.id,
    ...updated.data(),
  } as FlowDefinition;
};

// ============================================================================
// PUBLIC OPERATIONS (All Authenticated Users)
// ============================================================================

/**
 * Get all published, catalogue-visible primitives (tenant-facing view).
 *
 * Primitives tagged catalogueVisibility='internal' (sys_prim_*) or 'e2e'
 * (e2e_prim_*) are excluded here so the flow-builder picker and other
 * tenant surfaces never show them.
 */
export const getPublishedPrimitives = async (): Promise<FlowDefinition[]> => {
  const primitivesRef = collection(db, COLLECTION_NAME);
  const q = query(
    primitivesRef,
    where('isPublished', '==', true),
    where('catalogueVisibility', '==', 'catalogue'),
    orderBy('name', 'asc')
  );
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  } as FlowDefinition));
};

/**
 * Get a specific published, catalogue-visible primitive by ID.
 * Rejects internal / e2e primitives even if the ID is guessed or leaked.
 */
export const getPublishedPrimitive = async (id: string): Promise<FlowDefinition> => {
  const primitiveRef = doc(db, COLLECTION_NAME, id);
  const snapshot = await getDoc(primitiveRef);

  if (!snapshot.exists()) {
    throw new Error('Primitive not found');
  }

  const primitive = {
    id: snapshot.id,
    ...snapshot.data(),
  } as FlowDefinition;

  if (!primitive.isPublished) {
    throw new Error('Primitive not published');
  }

  if ((primitive as any).catalogueVisibility !== 'catalogue') {
    throw new Error('Primitive not visible to tenants');
  }

  return primitive;
};

/**
 * Get all categories from catalogue-visible primitives only.
 */
export const getCategories = async (): Promise<string[]> => {
  const primitivesRef = collection(db, COLLECTION_NAME);
  const q = query(
    primitivesRef,
    where('isPublished', '==', true),
    where('catalogueVisibility', '==', 'catalogue')
  );
  const snapshot = await getDocs(q);

  const categoriesSet = new Set<string>();
  snapshot.docs.forEach(doc => {
    const category = doc.data().category;
    if (category) {
      categoriesSet.add(category);
    }
  });

  return Array.from(categoriesSet).sort();
};

/**
 * Get catalogue-visible primitives by category.
 */
export const getPrimitivesByCategory = async (category: string): Promise<FlowDefinition[]> => {
  const primitivesRef = collection(db, COLLECTION_NAME);
  const q = query(
    primitivesRef,
    where('isPublished', '==', true),
    where('catalogueVisibility', '==', 'catalogue'),
    where('category', '==', category),
    orderBy('name', 'asc')
  );
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  } as FlowDefinition));
};

/**
 * Search primitives by name or description
 * Available to all authenticated users
 */
export const searchPrimitives = async (searchQuery: string): Promise<FlowDefinition[]> => {
  // Get all published primitives and filter client-side
  // Note: Firestore doesn't support full-text search natively
  const primitives = await getPublishedPrimitives();
  
  const lowerQuery = searchQuery.toLowerCase();
  return primitives.filter(primitive => 
    primitive.name.toLowerCase().includes(lowerQuery) ||
    (primitive.description && primitive.description.toLowerCase().includes(lowerQuery))
  );
};

// ============================================================================
// EXPORTS
// ============================================================================

const primitiveService = {
  // Admin
  getAllPrimitives,
  getPrimitive,
  createPrimitive,
  updatePrimitive,
  deletePrimitive,
  publishPrimitive,
  unpublishPrimitive,
  
  // Public
  getPublishedPrimitives,
  getPublishedPrimitive,
  getCategories,
  getPrimitivesByCategory,
  searchPrimitives,
};

export default primitiveService;

